import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    TextField,
    InputAdornment,
    Box,
    Avatar,
    Chip,
    Tooltip,
    Fade,
    Zoom,
    Grid,
    Dialog,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { SCUIButton, SCUILoadingLinear } from '../../atomic/atoms';
import { SCUIContent } from '../../atomic/organisms';
import { SCUIBreadCrumb } from '../../atomic/molecules';
import { useMannequin } from './context/MannequinContext';
import { MannequinQuery } from '../../services/studio/StudioQuery';
import { MannequinCommand } from '../../services/studio/StudioCommand';
import { MannequinDetailModal } from './components/MannequinDetailModal';
import Swal from 'sweetalert2';
import { format } from 'date-fns';
import './Mannequin.scss';

export const MannequinList = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const {
        currentPage,
        pageSize,
        searchKeyword,
        mannequinListData,
        isLoading,
        totalCount,
        setMannequinListData,
        setCurrentPage,
        setSearchKeyword,
        setIsLoading,
        setTotalCount,
    } = useMannequin();

    const [localSearchKeyword, setLocalSearchKeyword] = useState('');
    const [viewMode, setViewMode] = useState<'grid' | 'card'>('grid');
    const [activeFilter, setActiveFilter] = useState<'all' | 'adult' | 'child'>('all');
    const [isAiFilterActive, setIsAiFilterActive] = useState(false);
    const [statsData, setStatsData] = useState({
        total: 0,
        adult: 0,
        child: 0,
        aiTotal: 0,
        aiAdult: 0,
        aiChild: 0,
    });
    const [selectedMannequin, setSelectedMannequin] = useState<any>(null);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [photoViewOpen, setPhotoViewOpen] = useState(false);
    const [selectedPhoto, setSelectedPhoto] = useState<{ url: string; name: string } | null>(null);

    useEffect(() => {
        fetchMannequins();
    }, [currentPage, pageSize, searchKeyword, activeFilter, isAiFilterActive]);

    useEffect(() => {
        // Fetch total stats on component mount (only once)
        fetchTotalStats();
    }, []);

    const fetchTotalStats = async () => {
        try {
            // Fetch total count
            const totalResponse = await MannequinQuery.GetMannequins({
                Page: 1,
                PageSize: 1, // We only need count, not data
            });

            // Fetch adult count
            const adultResponse = await MannequinQuery.GetMannequins({
                Page: 1,
                PageSize: 1,
                IsAdult: true,
            });

            // Fetch child count
            const childResponse = await MannequinQuery.GetMannequins({
                Page: 1,
                PageSize: 1,
                IsAdult: false,
            });

            // Fetch AI total count
            const aiTotalResponse = await MannequinQuery.GetMannequins({
                Page: 1,
                PageSize: 1,
                IsAi: true,
            });

            // Fetch AI adult count
            const aiAdultResponse = await MannequinQuery.GetMannequins({
                Page: 1,
                PageSize: 1,
                IsAdult: true,
                IsAi: true,
            });

            // Fetch AI child count
            const aiChildResponse = await MannequinQuery.GetMannequins({
                Page: 1,
                PageSize: 1,
                IsAdult: false,
                IsAi: true,
            });

            setStatsData({
                total: totalResponse?.data?.totalCount || 0,
                adult: adultResponse?.data?.totalCount || 0,
                child: childResponse?.data?.totalCount || 0,
                aiTotal: aiTotalResponse?.data?.totalCount || 0,
                aiAdult: aiAdultResponse?.data?.totalCount || 0,
                aiChild: aiChildResponse?.data?.totalCount || 0,
            });
        } catch (error) {
            console.error('Failed to fetch stats:', error);
        }
    };

    const fetchMannequins = async () => {
        setIsLoading(true);
        try {
            const params: any = {
                Name: searchKeyword || undefined,
                Page: currentPage,
                PageSize: pageSize,
            };

            // Add IsAdult filter if not 'all'
            if (activeFilter !== 'all') {
                params.IsAdult = activeFilter === 'adult';
            }

            // Add IsAi filter if active
            if (isAiFilterActive) {
                params.IsAi = true;
            }

            console.log('🔍 Fetching mannequins with params:', params);
            console.log('🎯 Active filter:', activeFilter, 'AI:', isAiFilterActive);

            const response = await MannequinQuery.GetMannequins(params);

            console.log('📥 Response:', response);

            if (response?.data) {
                setMannequinListData(response.data);
                setTotalCount(response.data.totalCount || 0);

                // If the response has no items but we're not on page 1, go to page 1
                const totalPages = Math.ceil(response.data.totalCount / pageSize) || 1;
                if (currentPage > totalPages && totalPages > 0) {
                    console.log('⚠️ Current page out of bounds, resetting to page 1');
                    setCurrentPage(1);
                }
            }
        } catch (error) {
            console.error('❌ Error fetching mannequins:', error);
            Swal.fire({
                icon: 'error',
                title: t('general.anErrorOccured'),
                text: t('mannequin.errorFetchingList'),
            });
            // If error occurs and we're not on page 1, try page 1
            if (currentPage > 1) {
                console.log('⚠️ Error occurred, trying page 1');
                setCurrentPage(1);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalSearchKeyword(event.target.value);
    };

    const handleSearchKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            setSearchKeyword(localSearchKeyword);
            setCurrentPage(1);
        }
    };

    const handleChangePage = (_: unknown, newPage: number) => {
        setCurrentPage(newPage + 1); // TablePagination is 0-based, API is 1-based
    };

    const handleDelete = async (id: number, name: string) => {
        const result = await Swal.fire({
            title: t('general.areYouSure'),
            text: t('mannequin.deleteConfirmation', { name }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('general.delete'),
            cancelButtonText: t('general.cancel'),
        });

        if (result.isConfirmed) {
            try {
                setIsLoading(true);
                await MannequinCommand.DeleteMannequin(id);
                Swal.fire({
                    icon: 'success',
                    title: t('general.success'),
                    text: t('mannequin.deleteSuccess'),
                    timer: 1500,
                    showConfirmButton: false,
                });
                fetchMannequins();
                fetchTotalStats(); // Update stats after delete
            } catch {
                Swal.fire({
                    icon: 'error',
                    title: t('general.anErrorOccured'),
                    text: t('mannequin.deleteError'),
                });
                setIsLoading(false);
            }
        }
    };

    const handleEdit = (id: number) => {
        history.push(`/studio/mannequin/edit/${id}`);
    };

    const handleViewDetails = (mannequin: any) => {
        setSelectedMannequin(mannequin);
        setDetailModalOpen(true);
    };

    const handlePhotoClick = (photoUrl: string, name: string) => {
        if (photoUrl) {
            setSelectedPhoto({ url: photoUrl, name });
            setPhotoViewOpen(true);
        }
    };

    const handleClosePhotoView = () => {
        setPhotoViewOpen(false);
        setSelectedPhoto(null);
    };

    const handleAddNew = () => {
        history.push('/studio/mannequin/create');
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        try {
            return format(new Date(dateString), 'dd/MM/yyyy HH:mm');
        } catch {
            return '-';
        }
    };

    const handleFilterClick = (filter: 'all' | 'adult' | 'child') => {
        console.log('🎯 Filter clicked:', filter);
        setCurrentPage(1); // Reset to first page when filtering
        setActiveFilter(filter);
    };

    const statsCards = [
        {
            icon: '👤',
            label: isAiFilterActive ? t('mannequin.aiTotalMannequins') : t('mannequin.totalMannequins'),
            value: isAiFilterActive ? statsData.aiTotal : statsData.total,
            color: '#1976d2',
            filter: 'all' as const,
        },
        {
            icon: '👔',
            label: isAiFilterActive ? t('mannequin.aiAdult') : t('mannequin.adult'),
            value: isAiFilterActive ? statsData.aiAdult : statsData.adult,
            color: '#2e7d32',
            filter: 'adult' as const,
        },
        {
            icon: '👶',
            label: isAiFilterActive ? t('mannequin.aiChild') : t('mannequin.child'),
            value: isAiFilterActive ? statsData.aiChild : statsData.child,
            color: '#ed6c02',
            filter: 'child' as const,
        },
    ];

    return (
        <SCUIContent>
            <div className="mannequin-container">
                <SCUIBreadCrumb title={t('mannequin.title')} componentLink={'/studio/mannequin'} />

                <SCUILoadingLinear display={isLoading} />

                <Fade in={true} timeout={500}>
                    <div className="mannequin-header">
                        <div className="mannequin-header-title">
                            <AccountCircleIcon sx={{ fontSize: '32px', color: '#1976d2' }} />
                            <h1>{t('mannequin.list')}</h1>
                            <span className="badge">{statsData.total}</span>
                        </div>
                        <SCUIButton
                            onClick={handleAddNew}
                            type="primary"
                            buttonType="button"
                            sizeType="fixed"
                            size="m"
                            text={t('mannequin.addNew')}
                            showStartIcon
                            startIcon={<AddIcon sx={{ color: '#fff' }} />}
                        />
                    </div>
                </Fade>

                <Fade in={true} timeout={600}>
                    <div className="mannequin-stats-wrapper">
                        <div className="mannequin-stats">
                            {statsCards.map((stat, index) => (
                                <Zoom in={true} timeout={500 + index * 100} key={index}>
                                    <div
                                        className={`mannequin-stats-card ${activeFilter === stat.filter ? 'active' : ''}`}
                                        onClick={() => handleFilterClick(stat.filter)}
                                    >
                                        <div
                                            className="icon"
                                            style={{ backgroundColor: `${stat.color}20`, color: stat.color }}
                                        >
                                            {stat.icon}
                                        </div>
                                        <div className="content">
                                            <div className="label">{stat.label}</div>
                                            <div className="value">{stat.value}</div>
                                        </div>
                                    </div>
                                </Zoom>
                            ))}
                        </div>
                        <Zoom in={true} timeout={800}>
                            <div
                                className={`mannequin-ai-toggle ${isAiFilterActive ? 'active' : ''}`}
                                onClick={() => {
                                    setIsAiFilterActive(!isAiFilterActive);
                                    setCurrentPage(1);
                                }}
                            >
                                <SmartToyIcon sx={{ fontSize: 32 }} />
                                <div className="label">{t('mannequin.aiMannequins')}</div>
                            </div>
                        </Zoom>
                    </div>
                </Fade>

                <Box className="mannequin-search-box">
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <TextField
                            placeholder={t('mannequin.searchPlaceholder')}
                            value={localSearchKeyword}
                            onChange={handleSearchChange}
                            onKeyDown={handleSearchKeyPress}
                            fullWidth
                            variant="outlined"
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ fontSize: '20px', color: '#666' }} />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                },
                            }}
                        />
                        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                            <Tooltip title={t('mannequin.gridView')} arrow>
                                <IconButton
                                    onClick={() => setViewMode('grid')}
                                    sx={{
                                        bgcolor: viewMode === 'grid' ? '#1976d2' : '#f5f5f5',
                                        color: viewMode === 'grid' ? '#fff' : '#666',
                                        '&:hover': {
                                            bgcolor: viewMode === 'grid' ? '#1565c0' : '#e0e0e0',
                                        },
                                    }}
                                >
                                    <ViewListIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={t('mannequin.cardView')} arrow>
                                <IconButton
                                    onClick={() => setViewMode('card')}
                                    sx={{
                                        bgcolor: viewMode === 'card' ? '#1976d2' : '#f5f5f5',
                                        color: viewMode === 'card' ? '#fff' : '#666',
                                        '&:hover': {
                                            bgcolor: viewMode === 'card' ? '#1565c0' : '#e0e0e0',
                                        },
                                    }}
                                >
                                    <ViewModuleIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </Box>

                {viewMode === 'grid' ? (
                    <Fade in={true} timeout={700}>
                        <TableContainer className="mannequin-grid" sx={{ overflowX: 'hidden' }}>
                            <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell width="60">{t('mannequin.photo')}</TableCell>
                                        <TableCell width="200">{t('mannequin.name')}</TableCell>
                                        <TableCell width="150">{t('mannequin.measurements')}</TableCell>
                                        <TableCell width="140">{t('mannequin.physicalInfo')}</TableCell>
                                        <TableCell width="180">{t('mannequin.agencyName')}</TableCell>
                                        <TableCell width="140">{t('mannequin.lastShotDate')}</TableCell>
                                        <TableCell width="100">{t('mannequin.isAdult')}</TableCell>
                                        <TableCell width="100" align="right">
                                            {t('mannequin.actions')}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {mannequinListData?.items?.map((row: any, index: number) => (
                                        <Fade in={true} timeout={300 + index * 50} key={row.id}>
                                            <TableRow>
                                                <TableCell>
                                                    <Zoom in={true} timeout={500}>
                                                        {row.photoUrl ? (
                                                            <Avatar
                                                                src={row.photoUrl}
                                                                alt={row.name}
                                                                className="mannequin-avatar"
                                                                onClick={() => handlePhotoClick(row.photoUrl, row.name)}
                                                                sx={{
                                                                    width: 50,
                                                                    height: 50,
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.3s ease',
                                                                    '&:hover': {
                                                                        transform: 'scale(1.1)',
                                                                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
                                                                    },
                                                                }}
                                                            />
                                                        ) : (
                                                            <Avatar
                                                                className="mannequin-avatar"
                                                                sx={{
                                                                    width: 50,
                                                                    height: 50,
                                                                    bgcolor: row.isAdult ? '#1976d2' : '#ff9800',
                                                                    fontSize: '20px',
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {row.name?.charAt(0)}
                                                            </Avatar>
                                                        )}
                                                    </Zoom>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ fontWeight: 600, fontSize: '14px' }}>{row.name}</Box>
                                                    <Box sx={{ fontSize: '12px', color: '#666', mt: 0.5 }}>
                                                        {row.passNo || t('mannequin.noPassport')}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    {row.isAdult ? (
                                                        <Chip
                                                            label={`${row.chest}-${row.waist}-${row.hip}`}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: '#e3f2fd',
                                                                color: '#1976d2',
                                                                fontWeight: 500,
                                                            }}
                                                        />
                                                    ) : (
                                                        <span style={{ color: '#999' }}>-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ fontSize: '13px' }}>
                                                        <div>📏 {row.length} cm</div>
                                                        {row.weight && <div>⚖️ {row.weight} kg</div>}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={row.agencyName}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ borderRadius: '8px' }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ fontSize: '13px' }}>
                                                        {row.lastShotDate ? (
                                                            <>
                                                                <CalendarTodayIcon
                                                                    sx={{ fontSize: '14px', marginRight: '4px', verticalAlign: 'middle' }}
                                                                />
                                                                {formatDate(row.lastShotDate)}
                                                            </>
                                                        ) : (
                                                            <span style={{ color: '#999' }}>-</span>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={
                                                            row.isAdult ? t('mannequin.adult') : t('mannequin.child')
                                                        }
                                                        size="small"
                                                        className={`mannequin-badge mannequin-badge-${
                                                            row.isAdult ? 'adult' : 'child'
                                                        }`}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title={t('general.detail')} arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleViewDetails(row)}
                                                            className="mannequin-action-btn"
                                                            sx={{
                                                                color: '#9c27b0',
                                                                '&:hover': { bgcolor: '#f3e5f5' },
                                                            }}
                                                        >
                                                            <VisibilityIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={t('general.edit')} arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleEdit(row.id)}
                                                            className="mannequin-action-btn"
                                                            sx={{
                                                                color: '#1976d2',
                                                                '&:hover': { bgcolor: '#e3f2fd' },
                                                            }}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={t('general.delete')} arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDelete(row.id, row.name)}
                                                            className="mannequin-action-btn"
                                                            sx={{
                                                                color: '#d32f2f',
                                                                '&:hover': { bgcolor: '#ffebee' },
                                                            }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        </Fade>
                                    ))}
                                    {(!mannequinListData?.items || mannequinListData.items.length === 0) &&
                                        !isLoading && (
                                            <TableRow>
                                                <TableCell colSpan={9} align="center">
                                                    <Box sx={{ py: 8, color: '#999' }}>
                                                        <PeopleAltIcon sx={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
                                                        <div>{t('mannequin.noData')}</div>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Fade>
                ) : (
                    <Fade in={true} timeout={700}>
                        <Box sx={{ mt: 2 }}>
                            <Grid container spacing={3}>
                                {mannequinListData?.items?.map((row: any) => (
                                    <Grid
                                        key={row.id}
                                        size={{
                                            xs: 12,
                                            sm: 6,
                                            md: 4,
                                            lg: 3,
                                        }}
                                    >
                                        <Zoom in={true} timeout={300}>
                                            <Box
                                                className="mannequin-card-view"
                                                onClick={() => handleViewDetails(row)}
                                                sx={{
                                                    position: 'relative',
                                                    borderRadius: '16px',
                                                    overflow: 'hidden',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        transform: 'translateY(-8px)',
                                                        boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                                                        '& .mannequin-card-overlay': {
                                                            opacity: 1,
                                                        },
                                                    },
                                                }}
                                            >
                                                <Box
                                                    onClick={(e) => {
                                                        if (row.photoUrl) {
                                                            e.stopPropagation();
                                                            handlePhotoClick(row.photoUrl, row.name);
                                                        }
                                                    }}
                                                    sx={{
                                                        width: '100%',
                                                        height: '350px',
                                                        position: 'relative',
                                                        background: row.photoUrl
                                                            ? `url(${row.photoUrl})`
                                                            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        backgroundSize: 'cover',
                                                        backgroundPosition: 'center',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        cursor: row.photoUrl ? 'pointer' : 'default',
                                                    }}
                                                >
                                                    {!row.photoUrl && (
                                                        <Box
                                                            sx={{
                                                                fontSize: '80px',
                                                                color: '#fff',
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            {row.name?.charAt(0)}
                                                        </Box>
                                                    )}

                                                    <Box
                                                        className="mannequin-card-overlay"
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            background:
                                                                'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0) 100%)',
                                                            opacity: 0,
                                                            transition: 'opacity 0.3s ease',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            justifyContent: 'flex-end',
                                                            padding: '24px',
                                                        }}
                                                    >
                                                        <Box sx={{ color: '#fff', mb: 2 }}>
                                                            <Box sx={{ fontSize: '20px', fontWeight: 600, mb: 1 }}>
                                                                {row.name}
                                                            </Box>
                                                            {row.isAdult && (
                                                                <Box sx={{ fontSize: '14px', opacity: 0.9, mb: 1 }}>
                                                                    📐 {row.chest}-{row.waist}-{row.hip}
                                                                </Box>
                                                            )}
                                                            <Box sx={{ fontSize: '14px', opacity: 0.9, mb: 1 }}>
                                                                📏 {row.length} cm{' '}
                                                                {row.weight ? `• ⚖️ ${row.weight} kg` : ''}
                                                            </Box>
                                                            <Box sx={{ fontSize: '14px', opacity: 0.9, mb: 1 }}>
                                                                🏢 {row.agencyName}
                                                            </Box>
                                                            {row.lastShotDate && (
                                                                <Box sx={{ fontSize: '13px', opacity: 0.8 }}>
                                                                    📅 {formatDate(row.lastShotDate)}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                        <Box
                                                            sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}
                                                        >
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleEdit(row.id);
                                                                }}
                                                                sx={{
                                                                    bgcolor: 'rgba(255,255,255,0.2)',
                                                                    color: '#fff',
                                                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                                                                }}
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDelete(row.id, row.name);
                                                                }}
                                                                sx={{
                                                                    bgcolor: 'rgba(255,255,255,0.2)',
                                                                    color: '#fff',
                                                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                                                                }}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Box>
                                                    </Box>

                                                    <Chip
                                                        label={
                                                            row.isAdult ? t('mannequin.adult') : t('mannequin.child')
                                                        }
                                                        size="small"
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 12,
                                                            right: 12,
                                                            bgcolor: row.isAdult ? '#1976d2' : '#ff9800',
                                                            color: '#fff',
                                                            fontWeight: 600,
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        </Zoom>
                                    </Grid>
                                ))}
                                {(!mannequinListData?.items || mannequinListData.items.length === 0) && !isLoading && (
                                    <Grid
                                        size={{
                                            xs: 12,
                                        }}
                                    >
                                        <Box sx={{ py: 8, textAlign: 'center', color: '#999' }}>
                                            <PeopleAltIcon sx={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
                                            <div>{t('mannequin.noData')}</div>
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </Fade>
                )}

                {viewMode === 'grid' && (
                    <Box
                        sx={{
                            background: '#fff',
                            borderRadius: '0 0 12px 12px',
                            borderTop: '1px solid #e0e0e0',
                        }}
                    >
                        <TablePagination
                            component="div"
                            count={totalCount}
                            page={currentPage - 1}
                            onPageChange={handleChangePage}
                            rowsPerPage={pageSize}
                            rowsPerPageOptions={[]}
                            labelDisplayedRows={({ from, to, count }) =>
                                `${from}-${to} ${t('general.of')} ${
                                    count !== -1 ? count : `${t('general.moreThan')} ${to}`
                                }`
                            }
                            sx={{
                                '.MuiTablePagination-toolbar': {
                                    padding: '16px',
                                },
                            }}
                        />
                    </Box>
                )}

                {viewMode === 'card' && (
                    <Box
                        sx={{
                            background: '#fff',
                            borderRadius: '12px',
                            mt: 2,
                            p: 2,
                        }}
                    >
                        <TablePagination
                            component="div"
                            count={totalCount}
                            page={currentPage - 1}
                            onPageChange={handleChangePage}
                            rowsPerPage={pageSize}
                            rowsPerPageOptions={[]}
                            labelDisplayedRows={({ from, to, count }) =>
                                `${from}-${to} ${t('general.of')} ${
                                    count !== -1 ? count : `${t('general.moreThan')} ${to}`
                                }`
                            }
                            sx={{
                                '.MuiTablePagination-toolbar': {
                                    padding: '16px',
                                },
                            }}
                        />
                    </Box>
                )}
            </div>
            {/* Photo View Modal */}
            <Dialog
                open={photoViewOpen}
                onClose={handleClosePhotoView}
                maxWidth="lg"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            bgcolor: 'transparent',
                            boxShadow: 'none',
                            maxWidth: '90vw',
                        },
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        border: 'none !important',
                        borderBottom: 'none !important',
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <IconButton
                                onClick={handleClosePhotoView}
                                sx={{
                                    bgcolor: '#1976d2',
                                    color: 'white',
                                    '&:hover': {
                                        bgcolor: '#1565c0',
                                        transform: 'translateX(-2px)',
                                    },
                                    transition: 'all 0.3s ease',
                                }}
                            >
                                <ArrowBackIcon sx={{ fontSize: '24px' }} />
                            </IconButton>
                        </Box>
                        <IconButton
                            onClick={handleClosePhotoView}
                            size="small"
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.9)',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,1)',
                                },
                            }}
                        >
                            <CloseIcon sx={{ fontSize: '24px' }} />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent
                    sx={{
                        border: 'none !important',
                        borderTop: 'none !important',
                    }}
                >
                    <Fade in={true} timeout={500}>
                        <Box>
                            <Zoom in={true} timeout={700}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        minHeight: '500px',
                                        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                                        borderRadius: '12px',
                                        padding: '24px',
                                        position: 'relative',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {selectedPhoto?.url && (
                                        <img
                                            src={selectedPhoto.url}
                                            alt={selectedPhoto.name}
                                            style={{
                                                maxWidth: '100%',
                                                maxHeight: '70vh',
                                                objectFit: 'contain',
                                                borderRadius: '8px',
                                                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                                            }}
                                        />
                                    )}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            bottom: 24,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            background: 'rgba(255, 255, 255, 0.95)',
                                            padding: '16px 32px',
                                            borderRadius: '24px',
                                            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                                        }}
                                    >
                                        <Typography variant="h6" fontWeight={600} color="primary">
                                            {selectedPhoto?.name}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Zoom>
                        </Box>
                    </Fade>
                </DialogContent>
            </Dialog>
            {/* Detail Modal */}
            <MannequinDetailModal
                open={detailModalOpen}
                onClose={() => setDetailModalOpen(false)}
                mannequin={selectedMannequin}
            />
        </SCUIContent>
    );
};
